/**
 * Shared sync-freshness helpers - single source of truth across:
 *   - Inspector nav chips (SystemStatusChips)
 *   - Inspector alert stack freshness row (useInspectorAlerts)
 *   - Health page snapshot cards (SystemSnapshotCard)
 *   - Dashboard hero indicator (DashboardHero)
 *   - Platform reports "Data Age" column (tableFormatters.getSyncStatusColor)
 *
 * Tier ladder (canonical):
 *   < 12h           → fresh
 *   12h - 24h       → warning
 *   24h - 72h       → attention
 *   ≥ 72h, or null  → critical
 */

// tableFormatters imports getFreshnessTier from this file - keep all usage
// of formatTimeAgo lazy (inside function bodies) so the runtime circular
// import resolves via ESM live bindings. No top-level calls.
import { formatTimeAgo } from './tableFormatters'

export type Tier = 'fresh' | 'warning' | 'attention' | 'critical'

export const THRESHOLDS = {
  warningHours: 12,
  attentionHours: 24,
  criticalHours: 72,
} as const

export interface SyncFreshnessEntry {
  system: string
  last_completed: string | null
  days_ago: number | null
}

export interface SyncStatus {
  dotColor: string
  label: string
}

const TIER_ORDER: Record<Tier, number> = {
  fresh: 0,
  warning: 1,
  attention: 2,
  critical: 3,
}

const TIER_TO_DOT_COLOR: Record<Tier, string> = {
  fresh: 'success.main',
  warning: 'warning.light',
  attention: 'warning.main',
  critical: 'error.main',
}

const hasExplicitTz = (s: string): boolean =>
  s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s)

const parseDate = (dateStr: string): Date =>
  new Date(hasExplicitTz(dateStr) ? dateStr : `${dateStr}Z`)

const hoursSince = (dateStr: string): number => {
  const ms = Date.now() - parseDate(dateStr).getTime()
  return ms / (1000 * 60 * 60)
}

export function getFreshnessTier(dateStr: string | null | undefined): Tier {
  if (!dateStr) return 'critical'
  const hours = hoursSince(dateStr)
  if (hours < THRESHOLDS.warningHours) return 'fresh'
  if (hours < THRESHOLDS.attentionHours) return 'warning'
  if (hours < THRESHOLDS.criticalHours) return 'attention'
  return 'critical'
}

export function getWorstTier(tiers: Tier[]): Tier {
  if (tiers.length === 0) return 'fresh'
  return tiers.reduce<Tier>((worst, t) => (TIER_ORDER[t] > TIER_ORDER[worst] ? t : worst), 'fresh')
}

export function getOldestAge(dates: (string | null | undefined)[]): string | null {
  const present = dates.filter((d): d is string => Boolean(d))
  if (present.length === 0) return null
  const oldest = present.reduce((acc, d) =>
    parseDate(d).getTime() < parseDate(acc).getTime() ? d : acc
  )
  return formatTimeAgo(oldest)
}

/** Resolve the MUI palette colour key for the sync dot. */
export function syncDotColor(entry?: SyncFreshnessEntry | null): string {
  if (!entry?.last_completed || entry.days_ago === null) return 'text.disabled'
  const hours = (entry.days_ago ?? 0) * 24
  const tier: Tier =
    hours < THRESHOLDS.warningHours ? 'fresh'
    : hours < THRESHOLDS.attentionHours ? 'warning'
    : hours < THRESHOLDS.criticalHours ? 'attention'
    : 'critical'
  return TIER_TO_DOT_COLOR[tier]
}

/** Human-readable "Synced X ago" label for the sync caption. */
export function syncLabel(entry?: SyncFreshnessEntry | null): string {
  if (!entry?.last_completed) return 'No sync data'
  return `Synced ${formatTimeAgo(entry.last_completed)} ago`
}

/**
 * Convenience for callers that only have a `last_sync_at` ISO string.
 * Preserves the "No sync yet" wording for systems that have never synced
 * so the dashboard hero / snapshot card keep distinguishing unsynced
 * connections from stale ones.
 */
export function deriveSyncStatus(
  lastSyncAt: string | null | undefined,
  _system = 'unknown',
): SyncStatus {
  if (!lastSyncAt) {
    return { dotColor: 'text.disabled', label: 'No sync yet' }
  }
  const parsed = parseDate(lastSyncAt)
  if (Number.isNaN(parsed.getTime())) {
    return { dotColor: 'text.disabled', label: 'No sync data' }
  }
  const tier = getFreshnessTier(lastSyncAt)
  return {
    dotColor: TIER_TO_DOT_COLOR[tier],
    label: `Synced ${formatTimeAgo(lastSyncAt)} ago`,
  }
}
